import { evaluateFormulaSafe } from './evalFormula';

export const KPI_COLOR_STYLES = {
  blue: {
    tile: 'from-sky-50 via-white to-white',
    border: 'border-sky-100',
    iconBg: 'bg-sky-100 text-sky-700',
    accent: '#0284c7',
  },
  emerald: {
    tile: 'from-emerald-50 via-white to-white',
    border: 'border-emerald-100',
    iconBg: 'bg-emerald-100 text-emerald-700',
    accent: '#059669',
  },
  purple: {
    tile: 'from-violet-50 via-white to-white',
    border: 'border-violet-100',
    iconBg: 'bg-violet-100 text-violet-700',
    accent: '#7c3aed',
  },
  orange: {
    tile: 'from-orange-50 via-white to-white',
    border: 'border-orange-100',
    iconBg: 'bg-orange-100 text-orange-700',
    accent: '#ea580c',
  },
  indigo: {
    tile: 'from-indigo-50 via-white to-white',
    border: 'border-indigo-100',
    iconBg: 'bg-indigo-100 text-indigo-700',
    accent: '#4f46e5',
  },
  pink: {
    tile: 'from-pink-50 via-white to-white',
    border: 'border-pink-100',
    iconBg: 'bg-pink-100 text-pink-700',
    accent: '#db2777',
  },
  red: {
    tile: 'from-rose-50 via-white to-white',
    border: 'border-rose-100',
    iconBg: 'bg-rose-100 text-rose-700',
    accent: '#e11d48',
  },
  amber: {
    tile: 'from-amber-50 via-white to-white',
    border: 'border-amber-100',
    iconBg: 'bg-amber-100 text-amber-800',
    accent: '#d97706',
  },
  slate: {
    tile: 'from-slate-50 via-white to-white',
    border: 'border-slate-200',
    iconBg: 'bg-slate-100 text-slate-700',
    accent: '#475569',
  },
};

export const KPI_COLOR_OPTIONS = [
  { value: 'blue', label: 'آبی' },
  { value: 'emerald', label: 'سبز' },
  { value: 'purple', label: 'بنفش' },
  { value: 'orange', label: 'نارنجی' },
  { value: 'indigo', label: 'نیلی' },
  { value: 'pink', label: 'صورتی' },
  { value: 'red', label: 'قرمز' },
  { value: 'amber', label: 'کهربایی' },
  { value: 'slate', label: 'خاکستری' },
];

export const KPI_ICON_PRESETS = [
  '📦', '👥', '💳', '↩️', '🎯', '📊', '🧾', '🏷️', '💰', '⭐',
  '🔥', '📈', '🏢', '👤', '👔', '🏭', '🛒', '✅', '⚠️', '📍',
];

export const KPI_FORMAT_OPTIONS = [
  { value: 'number', label: 'عدد' },
  { value: 'percent', label: 'درصد' },
  { value: 'currency', label: 'ریال' },
  { value: 'text', label: 'متن' },
];

export const KPI_SIZE_OPTIONS = [
  { value: 'xs', label: 'خیلی کوچک', valueClass: 'text-base', labelClass: 'text-[10px]' },
  { value: 'sm', label: 'کوچک', valueClass: 'text-xl', labelClass: 'text-[11px]' },
  { value: 'md', label: 'متوسط', valueClass: 'text-2xl sm:text-[1.65rem]', labelClass: 'text-xs' },
  { value: 'lg', label: 'بزرگ', valueClass: 'text-3xl sm:text-4xl', labelClass: 'text-sm' },
  { value: 'xl', label: 'خیلی بزرگ', valueClass: 'text-4xl sm:text-5xl', labelClass: 'text-sm' },
];

export const KPI_SECTION_COLORS = [
  { value: '', label: 'خودکار' },
  { value: '#0f172a', label: 'مشکی' },
  { value: '#0284c7', label: 'آبی' },
  { value: '#059669', label: 'سبز' },
  { value: '#7c3aed', label: 'بنفش' },
  { value: '#ea580c', label: 'نارنجی' },
  { value: '#e11d48', label: 'قرمز' },
  { value: '#d97706', label: 'کهربایی' },
  { value: '#475569', label: 'خاکستری' },
];

function nextId(prefix, existing) {
  const used = new Set((existing || []).map((x) => x.id));
  let i = 1;
  while (used.has(`${prefix}_${i}`)) i += 1;
  return `${prefix}_${i}`;
}

export function emptyKpiLine(existing = []) {
  return {
    id: nextId('line', existing),
    ...emptyKpiSection({ size: 'sm' }),
  };
}

export function emptyKpiItem(existing = []) {
  return {
    id: nextId('kpi', existing),
    title: 'شاخص جدید',
    icon: '📊',
    color: 'blue',
    span: 1,
    valueKey: '',
    valueFormula: '',
    format: 'number',
    decimals: 0,
    useGrouping: true,
    align: 'right',
    bold: true,
    italic: false,
    subLabel: '',
    subKey: '',
    subFormula: '',
    badgeKey: '',
    badgeFormula: '',
    badgeDisplay: 'none',
    achievementMode: false,
    showFrame: true,
    showTitle: true,
    padded: true,
    fitContent: false,
    lines: [],
  };
}

/** Compact tile that sits in its layout cell without frame/header/padding. */
export function applyNakedKpiChrome(item, naked) {
  if (naked) {
    return { ...item, showFrame: false, showTitle: false, padded: false, fitContent: true };
  }
  return { ...item, showFrame: true, showTitle: true, padded: true, fitContent: false };
}

export function isNakedKpiChrome(item = {}) {
  return item.showFrame === false
    && item.showTitle === false
    && item.padded === false
    && !!item.fitContent;
}

/**
 * Default KPI grid — same formulas operators had in settings.dashboard_cards.
 * Widgets evaluate these against the dashboard metrics row.
 */
export function defaultDashboardKpiItems() {
  return [
    {
      ...emptyKpiItem([]),
      id: 'total_sale',
      title: 'فروش کل',
      icon: '📦',
      color: 'blue',
      valueFormula: 'targeted_sales',
      format: 'unit_breakdown',
      subLabel: 'هدف',
      subFormula: 'sale_target',
      badgeFormula: '(targeted_sales / sale_target) * 100',
      badgeDisplay: 'percent',
    },
    {
      ...emptyKpiItem([]),
      id: 'total_sale_amount',
      title: 'فروش کل ریال',
      icon: '💵',
      color: 'indigo',
      valueFormula: 'net_after_return',
      format: 'currency',
      decimals: 0,
    },
    {
      ...emptyKpiItem([]),
      id: 'active_customer_pct',
      title: 'مشتری فعال',
      icon: '👥',
      color: 'purple',
      valueFormula: 'customer_count_month',
      format: 'number',
      decimals: 0,
      badgeFormula: '(customer_count_month / customer_target_month) * 100',
      badgeDisplay: 'percent',
    },
    {
      ...emptyKpiItem([]),
      id: 'claims_pct',
      title: 'درصد مطالبات',
      icon: '💳',
      color: 'orange',
      valueFormula: '(financial_claims / total_net_amount) * 100',
      format: 'percent',
      decimals: 2,
      subLabel: 'مانده فاکتور باز (ریال)',
      subFormula: 'financial_claims',
    },
    {
      ...emptyKpiItem([]),
      id: 'return_pct',
      title: 'درصد مرجوعی',
      icon: '↩️',
      color: 'amber',
      valueFormula: '(total_gross_carton - total_net_carton) / total_gross_carton * 100',
      format: 'percent',
      decimals: 2,
      subLabel: 'کارتن ناخالص',
      subFormula: 'total_gross_carton',
    },
    {
      ...emptyKpiItem([]),
      id: 'commission_paid',
      title: 'پورسانت پرداختی',
      icon: '💰',
      color: 'emerald',
      valueFormula: 'commission_paid',
      format: 'currency',
      decimals: 0,
      subLabel: 'مصوب',
      subFormula: 'commission_allocated',
      badgeFormula: '(commission_paid / commission_allocated) * 100',
      badgeDisplay: 'percent',
    },
  ];
}

function asNumber(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function resolveNumeric(row, key, formula, fallback = null) {
  if (formula && String(formula).trim()) {
    return evaluateFormulaSafe(formula, row || {}, fallback ?? 0);
  }
  if (key) {
    const n = asNumber(row?.[key]);
    if (n != null) return n;
    return fallback;
  }
  return fallback;
}

export function resolveRaw(row, key, formula) {
  if (formula && String(formula).trim()) {
    return evaluateFormulaSafe(formula, row || {}, 0);
  }
  if (key && row && Object.prototype.hasOwnProperty.call(row, key)) {
    return row[key];
  }
  return null;
}

export function normalizeKpiItem(raw = {}, index = 0) {
  const span = Math.max(1, Math.min(4, Number(raw.span) || 1));
  const format = KPI_FORMAT_OPTIONS.some((f) => f.value === raw.format)
    ? raw.format
    : (raw.format === 'rial' ? 'currency' : 'number');
  return {
    id: String(raw.id || `kpi_${index + 1}`),
    title: raw.title || raw.label || `شاخص ${index + 1}`,
    icon: raw.icon || '📊',
    color: KPI_COLOR_STYLES[raw.color] ? raw.color : 'blue',
    span,
    valueKey: raw.valueKey || '',
    valueFormula: raw.valueFormula || raw.value_formula || '',
    format,
    decimals: raw.decimals != null ? Number(raw.decimals) : (format === 'percent' ? 1 : 0),
    useGrouping: raw.useGrouping !== false,
    align: raw.align || 'right',
    bold: raw.bold !== false,
    italic: !!raw.italic,
    subLabel: raw.subLabel || raw.sub_label || '',
    subKey: raw.subKey || '',
    subFormula: raw.subFormula || raw.sub_formula || '',
    badgeKey: raw.badgeKey || '',
    badgeFormula: raw.badgeFormula || raw.badge_formula || '',
    badgeDisplay: ['none', 'percent', 'number'].includes(raw.badgeDisplay || raw.badge_display)
      ? (raw.badgeDisplay || raw.badge_display)
      : 'none',
    achievementMode: !!raw.achievementMode,
    showFrame: raw.showFrame !== false,
    showTitle: raw.showTitle !== false,
    padded: raw.padded !== false,
    fitContent: !!raw.fitContent,
    lines: Array.isArray(raw.lines)
      ? raw.lines.map((line, i) => ({
        id: String(line.id || `line_${i + 1}`),
        ...normalizeSection(line, { size: 'sm' }),
      }))
      : [],
  };
}

/**
 * Accept both the new items[] grid and legacy single-value statConfig.
 */
export function normalizeStatConfig(statConfig = {}) {
  const columns = Math.max(1, Math.min(4, Number(statConfig.columns) || 3));
  if (Array.isArray(statConfig.items) && statConfig.items.length > 0) {
    return {
      columns,
      items: statConfig.items.map((item, i) => normalizeKpiItem(item, i)),
    };
  }

  const hasLegacy = statConfig.valueKey || statConfig.label || statConfig.valueFormula;
  if (!hasLegacy) {
    return { columns, items: [] };
  }

  return {
    columns,
    items: [
      normalizeKpiItem({
        id: 'legacy',
        title: statConfig.label || 'KPI',
        valueKey: statConfig.valueKey,
        valueFormula: statConfig.valueFormula,
        format: statConfig.format,
        decimals: statConfig.decimals,
        useGrouping: statConfig.useGrouping,
        align: statConfig.align,
        bold: statConfig.bold,
        italic: statConfig.italic,
        achievementMode: statConfig.achievementMode,
        subLabel: statConfig.subtitle,
      }, 0),
    ],
  };
}

export function evaluateKpiItem(item, row) {
  const value = resolveRaw(row, item.valueKey, item.valueFormula);
  const subValue = (item.subFormula || item.subKey)
    ? resolveNumeric(row, item.subKey, item.subFormula, null)
    : null;
  const badgeValue = item.badgeDisplay !== 'none' && (item.badgeFormula || item.badgeKey)
    ? resolveNumeric(row, item.badgeKey, item.badgeFormula, null)
    : null;
  const lines = (item.lines || []).map((line) => ({
    ...line,
    value: resolveRaw(row, line.key, line.formula),
  }));
  return { value, subValue, badgeValue, lines };
}

export function emptyKpiSection(partial = {}) {
  return {
    label: '',
    key: '',
    formula: '',
    format: 'number',
    decimals: 0,
    color: '',
    size: 'md',
    progressFormula: '',
    progressColor: '',
    ...partial,
  };
}

export function kpiSizeClass(size, kind = 'value') {
  const opt = KPI_SIZE_OPTIONS.find((s) => s.value === size) || KPI_SIZE_OPTIONS[2];
  return kind === 'label' ? opt.labelClass : opt.valueClass;
}

function normalizeSection(raw = {}, defaults = {}) {
  const format = KPI_FORMAT_OPTIONS.some((f) => f.value === raw.format)
    ? raw.format
    : (raw.format === 'rial' ? 'currency' : (defaults.format || 'number'));
  return {
    label: raw.label || defaults.label || '',
    key: raw.key || raw.valueKey || '',
    formula: raw.formula || raw.valueFormula || '',
    format,
    decimals: raw.decimals != null ? Number(raw.decimals) : (format === 'percent' ? 1 : 0),
    color: raw.color || '',
    size: KPI_SIZE_OPTIONS.some((s) => s.value === raw.size) ? raw.size : (defaults.size || 'md'),
    progressFormula: raw.progressFormula || raw.progress_formula || '',
    progressColor: raw.progressColor || '',
  };
}

function itemToCard(item = {}) {
  const badgeFormat = item.badgeDisplay === 'percent' ? 'percent' : (item.badgeDisplay === 'number' ? 'number' : 'percent');
  return {
    icon: item.icon || '📊',
    color: item.color || 'blue',
    showFrame: item.showFrame !== false,
    showTitle: item.showTitle !== false,
    padded: item.padded !== false,
    fitContent: !!item.fitContent,
    value: normalizeSection({
      key: item.valueKey,
      formula: item.valueFormula,
      format: item.format === 'unit_breakdown' ? 'number' : item.format,
      decimals: item.decimals,
      color: '',
      size: 'lg',
      progressFormula: item.progressFormula || '',
    }, { size: 'lg' }),
    subtitleLeft: normalizeSection({
      label: item.subLabel,
      key: item.subKey,
      formula: item.subFormula,
      format: 'number',
      size: 'sm',
      progressFormula: item.subProgressFormula || '',
    }, { size: 'sm' }),
    subtitleRight: normalizeSection(item.subtitleRight || {}, { size: 'sm' }),
    badge: normalizeSection({
      key: item.badgeKey,
      formula: item.badgeFormula,
      format: badgeFormat,
      size: 'sm',
      progressFormula: item.badgeProgressFormula || '',
    }, { size: 'sm', format: 'percent' }),
    lines: (item.lines || []).map((line, i) => ({
      id: String(line.id || `line_${i + 1}`),
      ...normalizeSection(line, { size: 'sm' }),
    })),
  };
}

/** Single-card KPI config. Legacy items[] still render as a grid in the viewer. */
export function normalizeKpiCard(statConfig = {}) {
  if (statConfig && (statConfig.value || statConfig.subtitle || statConfig.subtitleLeft || statConfig.badge)
    && !statConfig.items && !statConfig.valueKey && !statConfig.valueFormula) {
    const left = statConfig.subtitleLeft || statConfig.subtitle || {};
    return {
      icon: statConfig.icon || '📊',
      color: KPI_COLOR_STYLES[statConfig.color] ? statConfig.color : 'blue',
      showFrame: statConfig.showFrame !== false,
      showTitle: statConfig.showTitle !== false,
      padded: statConfig.padded !== false,
      fitContent: !!statConfig.fitContent,
      value: normalizeSection(statConfig.value || {}, { size: 'lg' }),
      subtitleLeft: normalizeSection(left, { size: 'sm' }),
      subtitleRight: normalizeSection(statConfig.subtitleRight || {}, { size: 'sm' }),
      badge: normalizeSection(statConfig.badge || {}, { size: 'sm', format: 'percent' }),
      lines: Array.isArray(statConfig.lines)
        ? statConfig.lines.map((line, i) => ({
          id: String(line.id || `line_${i + 1}`),
          ...normalizeSection(line, { size: 'sm' }),
        }))
        : [],
    };
  }
  const { items } = normalizeStatConfig(statConfig);
  if (items[0]) return itemToCard(items[0]);
  return itemToCard(emptyKpiItem([]));
}

export function emptyKpiCard() {
  return {
    icon: '📊',
    color: 'blue',
    showFrame: true,
    showTitle: true,
    padded: true,
    fitContent: false,
    value: emptyKpiSection({ size: 'lg', format: 'number' }),
    subtitleLeft: emptyKpiSection({ size: 'sm', label: '' }),
    subtitleRight: emptyKpiSection({ size: 'sm', label: '' }),
    badge: emptyKpiSection({ size: 'sm', format: 'percent' }),
    lines: [],
  };
}

export function evaluateKpiSection(section, row) {
  if (!section) return { value: null, progress: null };
  const value = resolveRaw(row, section.key, section.formula);
  const progress = section.progressFormula && String(section.progressFormula).trim()
    ? resolveNumeric(row, '', section.progressFormula, null)
    : null;
  return { value, progress };
}

export function evaluateKpiCard(card, row) {
  const subtitleLeft = evaluateKpiSection(card.subtitleLeft || card.subtitle, row);
  const subtitleRight = evaluateKpiSection(card.subtitleRight, row);
  return {
    value: evaluateKpiSection(card.value, row),
    subtitleLeft,
    subtitleRight,
    subtitle: subtitleLeft,
    badge: evaluateKpiSection(card.badge, row),
    lines: (card.lines || []).map((line) => ({
      ...line,
      ...evaluateKpiSection(line, row),
    })),
  };
}

export function keysFromKpiCard(card = {}) {
  const keys = [];
  const add = (section) => {
    if (section?.key) keys.push(section.key);
  };
  add(card.value);
  add(card.subtitleLeft || card.subtitle);
  add(card.subtitleRight);
  add(card.badge);
  (card.lines || []).forEach(add);
  return keys.filter((k, i, a) => k && a.indexOf(k) === i);
}
